// Config-driven questionnaire. One question per screen.
// type: "choice" | "money" | "number"

export const STEPS = [
  {
    id: 1,
    key: "salon_type",
    type: "choice",
    section: "Your salon",
    question: "What type of salon do you operate?",
    options: [
      { value: "Hair salon", label: "Hair salon" },
      { value: "Barber shop", label: "Barber shop" },
      { value: "Hair and beauty salon", label: "Hair and beauty salon" },
      { value: "Specialist colour salon", label: "Specialist colour salon" },
      { value: "Chair rental salon", label: "Chair rental salon" },
      { value: "Other", label: "Other" },
    ],
  },
  {
    id: 2,
    key: "annual_revenue",
    type: "money",
    section: "Earnings",
    note:
      "To value a salon we use Seller's Discretionary Earnings (SDE) — the income accruing to the owner, being profit plus any payments made to the owner such as salary, vehicle and superannuation.",
    question:
      "What was the salon's total revenue in its most recently completed financial year?",
    placeholder: "e.g. 420,000",
  },
  {
    id: 3,
    key: "net_profit",
    type: "money",
    section: "Earnings",
    question:
      "What was the salon's net profit before tax in its most recently completed financial year?",
    help: "Use the profit shown after normal business expenses, but before income tax.",
    placeholder: "e.g. 85,000",
  },
  {
    id: 4,
    key: "owner_wages",
    type: "money",
    section: "Earnings",
    question:
      "How much salary, wages and superannuation were paid to the working owner during that financial year?",
    help: "Include the total paid to one working owner through wages, salary and employer superannuation.",
    placeholder: "e.g. 60,000",
  },
  {
    id: 5,
    key: "multiple_owners",
    type: "choice",
    section: "Earnings",
    question: "Does this amount relate to more than one working owner?",
    options: [
      { value: "one", label: "No, one working owner" },
      { value: "two_plus", label: "Yes, two or more working owners" },
      { value: "not_sure", label: "Not sure" },
    ],
  },
  {
    id: 5.5,
    key: "revenue_growth",
    type: "choice",
    section: "Earnings",
    question: "How has revenue changed compared with the previous year?",
    help: "Revenue trend helps us gauge momentum and buyer appeal.",
    options: [
      { value: "declined", label: "Declined" },
      { value: "flat", label: "About the same" },
      { value: "up_10", label: "Grew up to 10%" },
      { value: "up_25", label: "Grew 10% - 25%" },
      { value: "up_25plus", label: "Grew more than 25%" },
      { value: "not_sure", label: "Not sure" },
    ],
  },
  {
    id: 6,
    key: "owner_hours",
    type: "choice",
    section: "Owner involvement",
    question: "How many hours per week does the owner normally work in the salon?",
    options: [
      { value: "<10", label: "Less than 10 hours" },
      { value: "10-20", label: "10 - 20 hours" },
      { value: "21-30", label: "21 - 30 hours" },
      { value: "31-40", label: "31 - 40 hours" },
      { value: ">40", label: "More than 40 hours" },
    ],
  },
  {
    id: 7,
    key: "owner_revenue_pct",
    type: "choice",
    section: "Owner involvement",
    question: "How much of the salon's service revenue is personally generated by the owner?",
    options: [
      { value: "<10", label: "Less than 10%" },
      { value: "10-25", label: "10% - 25%" },
      { value: "26-50", label: "26% - 50%" },
      { value: ">50", label: "More than 50%" },
      { value: "not_sure", label: "Not sure" },
    ],
  },
  {
    id: 8,
    key: "team_size",
    type: "number",
    section: "The team",
    question: "How many people work in the salon, excluding the owner?",
    help: "Include employees, apprentices and regular contractors.",
    placeholder: "e.g. 4",
  },
  {
    id: 9,
    key: "key_stylist_reliance",
    type: "choice",
    section: "The team",
    question: "Is the salon heavily reliant on one stylist other than the owner?",
    options: [
      { value: "no", label: "No" },
      { value: "somewhat", label: "Somewhat" },
      { value: "yes", label: "Yes" },
      { value: "not_sure", label: "Not sure" },
    ],
  },
  {
    id: 10,
    key: "total_stations",
    type: "number",
    section: "Capacity",
    question: "How many styling stations does the salon have?",
    placeholder: "e.g. 6",
  },
  {
    id: 11,
    key: "used_stations",
    type: "number",
    section: "Capacity",
    question: "How many of those stations are regularly used?",
    placeholder: "e.g. 4",
  },
  {
    id: 12,
    key: "can_add_stylists",
    type: "choice",
    section: "Capacity",
    question: "Could additional stylists use the vacant stations without a major fit-out?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "not_sure", label: "Not sure" },
    ],
  },
  {
    id: 13,
    key: "lease_years",
    type: "choice",
    section: "The lease",
    question: "How many years remain on the lease, including available options?",
    options: [
      { value: "<1", label: "Less than one year" },
      { value: "1-2", label: "1 - 2 years" },
      { value: "3-5", label: "3 - 5 years" },
      { value: ">5", label: "More than five years" },
      { value: "none", label: "No formal lease" },
      { value: "not_sure", label: "Not sure" },
    ],
  },
  {
    id: 13.5,
    key: "lease_renewal_likely",
    type: "choice",
    section: "The lease",
    showIf: (a) => a.lease_years === "<1",
    question: "Do you believe the landlord will be open to a new lease for a new buyer?",
    help: "A landlord willing to grant a fresh lease can protect much of a salon's value, even when the current term is short.",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: 14,
    key: "annual_rent",
    type: "money",
    section: "The lease",
    question: "What is the total annual rent and outgoings?",
    placeholder: "e.g. 45,000",
  },
  {
    id: 15,
    key: "fitout_cost",
    type: "money",
    section: "Fit-out",
    question: "How much was the original fit-out?",
    placeholder: "e.g. 120,000",
  },
  {
    id: 16,
    key: "fitout_year",
    type: "number",
    section: "Fit-out",
    question: "What year was the fit-out done?",
    placeholder: "e.g. 2020",
  },
  {
    id: 17,
    key: "fitout_condition",
    type: "choice",
    section: "Fit-out",
    question: "What condition is the salon fit-out and equipment in?",
    options: [
      { value: "excellent", label: "Excellent" },
      { value: "good", label: "Good" },
      { value: "fair", label: "Fair" },
      { value: "poor", label: "Poor" },
    ],
  },
  {
    id: 18,
    key: "refurb_needed",
    type: "choice",
    section: "Fit-out",
    question:
      "Will major refurbishment or equipment replacement probably be required within two years?",
    options: [
      { value: "no", label: "No" },
      { value: "possibly", label: "Possibly" },
      { value: "yes", label: "Yes" },
    ],
  },
];

export const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export const SALE_TIMING = [
  { value: "Now", label: "Now" },
  { value: "Within six months", label: "Within six months" },
  { value: "Within twelve months", label: "Within twelve months" },
  { value: "More than twelve months away", label: "More than twelve months away" },
  { value: "Just curious", label: "Just curious" },
];
